import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { ChevronLeftIcon, ClipboardIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { createOrUpdateMissionToStaffTravelExpenses } from '../../api/mission-to-staffs/create-or-update-travel-expenses'
import { getMissionToStaff } from '../../api/mission-to-staffs/get-mission-to-staff'
import { openMissionToStaffTravelExpenses } from '../../api/mission-to-staffs/open-mission-to-staff-travel-expenses'
import { sendMissionToStaffTravelExpensesToLec } from '../../api/mission-to-staffs/send-travel-expenses-to-lec'
import { validateMissionToStaffsTravelExpenses } from '../../api/mission-to-staffs/validate-travel-expenses'
import { EditableTableColumn } from '../../components/elements/editable-table'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { TravelExpensesTable } from '../../components/pages/staff/staff-personal-space/travel-expenses.ui'
import {
  getTravelExpensesColumns,
  mapTravelExpensesInputToRequest
} from '../../components/pages/staff/staff-personal-space/travel-expenses.utils'
import MissionToStaffProfile from '../../components/pages/travel-expenses/mission-to-staff-profile'
import TravelExpensesCommentsTable from '../../components/pages/travel-expenses/travel-expenses-comment-table'
import TravelExpensesCurrencyInput from '../../components/pages/travel-expenses/travel-expenses-currency-input'
import {
  TravelExpensesInvoiceButton,
  TravelExpensesValidateButton
} from '../../components/pages/travel-expenses/travel-expenses-validate-elements'
import { Paths } from '../../lib/constants/path'
import useTranslate from '../../lib/hooks/use-translate'
import { useTravelExpenses } from '../../lib/hooks/use-travel-expenses'
import {
  TravelExpensesStatusEnum,
  travelExpensesStatusEnumMapping
} from '../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { TravelExpenseTypeEnum } from '../../lib/types/enums/travel-expense/travel-expense.enum'
import { TravelExpenseInput } from '../../lib/types/models/travel-expenses/travel-expenses'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

export function TravelExpensesMissionToStaffDetailsPage() {
  const { id } = useParams()
  const { t } = useTranslate()
  const { navigate, back } = useNavigator()

  const {
    travelExpenses,
    preparatoryData,
    tripData,
    addDocument,
    deleteDocument,
    validateTravelExpense,
    invalidateTravelExpense,
    deleteTravelExpense,
    onDataChange,
    addNewTravelExpense,
    refetchTravelExpenses,
    isTravelExpensesValidate
  } = useTravelExpenses(Number(id), 'mission-to-staff')

  if (!id || isNaN(parseInt(id))) return <ItemNotFound title={'Identifiant de barème invalide'} />

  const { data, isPending, refetch } = useQuery({
    queryKey: ['travel-expenses-mission-to-staff', id],
    queryFn: () => getMissionToStaff(parseInt(id)),
    enabled: !!id
  })

  const saveTravelExpenses = useMutation({
    mutationFn: (input: TravelExpenseInput[]) => {
      if (data)
        return createOrUpdateMissionToStaffTravelExpenses(
          data.id,
          input.map(mapTravelExpensesInputToRequest)
        )
      return Promise.resolve([])
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('travel.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const validateTravelExpensesMutation = useMutation({
    mutationFn: async (input: TravelExpenseInput[]) => {
      if (data) {
        await validateMissionToStaffsTravelExpenses(
          +id,
          input.map(mapTravelExpensesInputToRequest)
        )
        await refetch()
      }
      return Promise.resolve([])
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('travel.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const openTravelExpensesMutation = useMutation({
    mutationFn: () => openMissionToStaffTravelExpenses(+id),
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('travel.expenses.submission.success'))
      navigate(Paths.STAFF_EXPENSE_REPORT_LIST_PAGE)
    }
  })

  const sendTravelExpensesToLecMutation = useMutation({
    mutationFn: (input: TravelExpenseInput[]) => {
      if (data)
        return sendMissionToStaffTravelExpensesToLec(
          data.id,
          input.map(mapTravelExpensesInputToRequest)
        )
      return Promise.resolve([])
    },
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('staff.personal.space.my.journeys.details.expenses.submission.success'))
      refetchTravelExpenses()
    }
  })

  const editable = data?.travelExpensesStatus === TravelExpensesStatusEnum.RECEIVED

  const columns: EditableTableColumn<TravelExpenseInput>[] = [
    ...getTravelExpensesColumns(t, ['date', 'details', 'amount', 'validatedAmount'], 'admin'),
    {
      header: t('travel.expenses.table.header.proof'),
      accessorKey: 'proof',
      centerCell: true,
      cell: ({ row }) => (
        <TravelExpensesInvoiceButton
          input={row.original}
          editable={editable && !row.original.validated}
          addDocument={addDocument}
          deleteDocument={deleteDocument}
        />
      )
    },
    {
      header: ' ',
      accessorKey: 'id',
      hidden: !editable,
      centerCell: true,
      cell: ({ row }) => (
        <TravelExpensesValidateButton
          input={row.original}
          validate={validateTravelExpense}
          invalidate={invalidateTravelExpense}
          remove={deleteTravelExpense}
        />
      )
    }
  ]

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />
  return (
    <div>
      <UserNavBar title={'Gestion notes de frais'} icon={ClipboardIcon} />
      <div className="h-4" />
      <Box className={'flex flex-col gap-4'}>
        <Button
          icon={ChevronLeftIcon}
          label="Retour"
          variant="secondary"
          className=" w-fit"
          onClick={() => back()}
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* <Typography variant="h3">Note de Frais</Typography> */}
            <Badge
              text={travelExpensesStatusEnumMapping[data.travelExpensesStatus].label}
              variant={travelExpensesStatusEnumMapping[data.travelExpensesStatus].variant}
            />
          </div>
        </div>
        <MissionToStaffProfile missionToStaff={data} />
        <Box style="border" className="rounded-lg">
          <div className="flex justify-end items-center">
            <TravelExpensesCurrencyInput readonly currency={data.travelExpensesCurrency} />
          </div>
          <TravelExpensesTable
            title={t('travel.expenses.preparatory.table.title')}
            addText={t('travel.expenses.preparatory.table.footer.add.label')}
            columns={columns}
            data={preparatoryData.map((d) => ({
              ...d,
              rowColor: d.validated ? tailwindColors.green.light.DEFAULT : undefined
            }))}
            isEditable={editable}
            onDataChange={onDataChange}
            type={TravelExpenseTypeEnum.PREPARATORY}
            onAddNewTravelExpense={addNewTravelExpense}
          />
          <TravelExpensesTable
            title={t('travel.expenses.trip.table.title')}
            addText={t('travel.expenses.trip.table.footer.add.label')}
            data={tripData.map((d) => ({
              ...d,
              rowColor: d.validated ? tailwindColors.green.light.DEFAULT : undefined
            }))}
            columns={columns}
            isEditable={editable}
            onDataChange={onDataChange}
            type={TravelExpenseTypeEnum.TRIP}
            onAddNewTravelExpense={addNewTravelExpense}
          />
          <Box
            style="border"
            className="bg-white-active flex-row items-center mobile:flex-col mobile:items-start tab:flex-col tab:items-start"
          >
            <Typography variant="text">
              {t('staff.personal.space.my.journeys.details.expenses.flat.fees', {
                flatFees: NumberFormatter.formatCurrency(170)
              })}
            </Typography>

            <Badge
              variant="grey"
              size="medium"
              borderColor={tailwindColors.grey.light.hover}
              text={
                <div className="flex items-center gap-2">
                  <Typography variant="caption">Total déclaré par le ME</Typography>

                  <Badge
                    text={NumberFormatter.formatCurrency(
                      travelExpenses.reduce((sum, te) => sum + (te.amount ?? 0), 0),
                      data.travelExpensesCurrency,
                      'narrowSymbol'
                    )}
                    variant="warning"
                    borderColor={tailwindColors.secondary.light.hover}
                  />
                </div>
              }
            />

            <Badge
              variant="grey"
              size="medium"
              borderColor={tailwindColors.grey.light.hover}
              text={
                <div className="flex items-center gap-2">
                  <Typography variant="caption">Total validé par LEC</Typography>

                  <Badge
                    text={NumberFormatter.formatCurrency(
                      travelExpenses.reduce((sum, te) => sum + (te.validatedAmount ?? 0), 0),
                      data.travelExpensesCurrency,
                      'narrowSymbol'
                    )}
                    variant="white"
                    borderColor={tailwindColors.secondary.light.hover}
                  />
                </div>
              }
            />
          </Box>
        </Box>
        {data.travelExpensesStatus === TravelExpensesStatusEnum.RECEIVED && (
          <div className="flex flex-row gap-2 justify-end">
            <Button
              variant="ghost"
              label={t('travel.expenses.open.for.staff')}
              loading={openTravelExpensesMutation.isPending}
              disabled={openTravelExpensesMutation.isPending || !travelExpenses.length}
              onClick={async () => {
                await openTravelExpensesMutation.mutateAsync()
              }}
            />
            <Button
              onClick={() => {
                if (!isTravelExpensesValidate(travelExpenses, 'save')) {
                  toast.error(t('travel.expenses.validation.error'))
                  return
                }
                saveTravelExpenses.mutateAsync(travelExpenses)
              }}
              variant="ghost"
              loading={saveTravelExpenses.isPending}
              disabled={saveTravelExpenses.isPending || !travelExpenses?.length}
              label={t('travel.expenses.validation.save')}
            />
            <Button
              variant="primary"
              label={t('travel.expenses.validate')}
              loading={validateTravelExpensesMutation.isPending}
              disabled={validateTravelExpensesMutation.isPending || !travelExpenses.length}
              onClick={async () => {
                if (!isTravelExpensesValidate(travelExpenses, 'submit')) {
                  toast.error(t('travel.expenses.validation.error'))
                  return
                }
                await validateTravelExpensesMutation.mutateAsync(travelExpenses)
              }}
            />
          </div>
        )}
        {data.travelExpensesStatus === TravelExpensesStatusEnum.VALIDATED && (
          <div className="flex flex-row gap-2 justify-end">
            <Button
              variant="primary"
              label={t('travel.expenses.open')}
              loading={sendTravelExpensesToLecMutation.isPending}
              disabled={sendTravelExpensesToLecMutation.isPending || !travelExpenses.length}
              onClick={async () => {
                await sendTravelExpensesToLecMutation.mutateAsync(travelExpenses)
                await refetch()
              }}
            />
          </div>
        )}
      </Box>
      <div className="h-4" />
      <Box>
        <TravelExpensesCommentsTable id={data.id} />
      </Box>
    </div>
  )
}
